import { useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  LinearProgress,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { abyssColors } from '@/theme/colors';
import {
  BodyCell,
  CardContainer,
  CardHeader,
  CardTitle,
  DateCellContent,
  DateText,
  DayText,
  HeaderCell,
  StyledTable,
  StyledTableContainer,
  TotalRow,
} from '../../styles';
import { useAtomValue } from 'jotai';
import * as kpiState from '../../state';
import { useFetchWorklogTemplatesEffect } from '../../hooks/useFetchWorklogTemplatesEffect';
import {
  aggregatedKpiFieldTotalMatchesColumn,
  convertValueByUnit,
  formatLogDate,
  getAggregatedKpiFieldNumericValue,
  getSectionUnitSymbol,
  mergeKpiAggregatedTemplateFields,
} from '../../utils';
import {
  useGetAggregatedKpiLogEntriesQuery,
  AllStructuresForHomePageQuery,
  SectionUnitsEnum,
  LogCategory,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import * as globalState from '@/components/Analysis/state';
import { ColumnsButton } from './ColumnsButton';
import { ExportLogsButton } from './ExportLogsButton';
import { LogWorkDrawer } from './LogWorkDrawer';
import { LogsButton } from './LogsButton';
import { ColumnsSelectionDialog } from '@/components/Analysis/Insights/ColumnsSelectionDialog';
import { useKpiXlsxExport } from '../../hooks/useKpiXlsxExport';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { COLLECTION_NAMES } from '@/constants';
import { useGetPlatformId } from '@/hooks/useGetPlatformId';
import AddIcon from '@mui/icons-material/Add';
import { containedButtonStyle } from '@/theme';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';

const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Processed row type based on dynamic fields
type ProcessedRow = {
  id: string;
  logDate: string;
  dayOfWeek: string;
  formattedDate: string;
  fieldValues: Record<string, number | undefined>;
};

type Props = {
  deployments: AllStructuresForHomePageQuery['allStructures'];
};

export const KpiTable = ({ deployments: allDeployments }: Props) => {
  const platformId = useGetPlatformId();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [exportXlsxDialogOpen, setExportXlsxDialogOpen] = useState(false);
  const visibleColumnKeys = useAtomValue(kpiState.visibleColumnKeys);
  const selectedDeploymentIds = useAtomValue(kpiState.selectedDeploymentIds);
  const selectedWorklogsTemplate = useAtomValue(kpiState.selectedWorklogsTemplate);
  const selectedIndirectPobLogsTemplateForKpi = useAtomValue(
    kpiState.selectedIndirectPobLogsTemplateForKpi
  );
  const selectedCostLogsTemplateForKpi = useAtomValue(kpiState.selectedCostLogsTemplateForKpi);
  const selectedDateAggregation = useAtomValue(kpiState.selectedDateAggregation);
  const selectedStartDate = useAtomValue(kpiState.selectedStartDate);
  const selectedEndDate = useAtomValue(kpiState.selectedEndDate);
  const selectedWorkpackTypes = useAtomValue(kpiState.selectedWorkpackTypes);
  const selectedWbsFilter = useAtomValue(kpiState.selectedWbsFilter);

  const selectedWorkpackTypesFilter = useMemo(() => {
    if (selectedWorkpackTypes.length === 0) return undefined;
    return selectedWorkpackTypes;
  }, [selectedWorkpackTypes]);

  const startDate = selectedStartDate
    ? new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(selectedStartDate)
    : undefined;

  const endDate = selectedEndDate
    ? new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(selectedEndDate)
    : undefined;

  const allStructuresIds = useMemo(
    () => allDeployments.filter(Boolean).map((deployment) => deployment.id),
    [allDeployments]
  );

  const { loading: templateLoading } = useFetchWorklogTemplatesEffect({
    structureIds: allStructuresIds,
  });
  const unitSystem = useAtomValue(globalState.unitSystem);
  const { downloadXLSX, loading: exportLoading } = useKpiXlsxExport();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    data: logsData,
    loading: logsLoading,
    error,
  } = useGetAggregatedKpiLogEntriesQuery({
    variables: {
      input: {
        collectionNames: [COLLECTION_NAMES.WORKPACK, COLLECTION_NAMES.PLATFORM],
        platformId,
        timezone: userTimezone,
        structureIds: selectedDeploymentIds,
        groupBy: selectedDateAggregation,
        startDate: startDate,
        endDate: endDate,
        workpackTypes: selectedWorkpackTypesFilter,
        wbs: selectedWbsFilter,
        category: [LogCategory.Worklog, LogCategory.IndirectPobLog, LogCategory.CostLog],
      },
      pageInfo: {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !selectedDeploymentIds.length || !platformId,
  });

  const hasAggregatedLogsData = logsData?.getAggregatedKpiLogEntries !== undefined;
  const isInitialLoading = templateLoading || (logsLoading && !hasAggregatedLogsData);
  const isRefreshing = logsLoading && hasAggregatedLogsData;
  const canQueryLogs = selectedDeploymentIds.length > 0 && Boolean(platformId);

  const templateFields = useMemo(
    () =>
      mergeKpiAggregatedTemplateFields(
        selectedWorklogsTemplate,
        selectedIndirectPobLogsTemplateForKpi,
        selectedCostLogsTemplateForKpi
      ),
    [
      selectedCostLogsTemplateForKpi,
      selectedIndirectPobLogsTemplateForKpi,
      selectedWorklogsTemplate,
    ]
  );

  // Get only the visible fields for rendering
  const visibleFields = useMemo(() => {
    return templateFields.filter(
      (field) => field.key && (visibleColumnKeys ?? []).includes(field.key)
    );
  }, [templateFields, visibleColumnKeys]);

  // Helper to convert value based on field's unit
  const getDisplayValue = (
    value: number | undefined,
    fieldUnit: SectionUnitsEnum | null | undefined,
    decimals?: number
  ): number | string => {
    if (value === undefined) return '-';
    const isArea = fieldUnit === SectionUnitsEnum.Area;
    const convertedValue = convertValueByUnit(
      value,
      fieldUnit,
      unitSystem,
      'view',
      isArea && decimals !== undefined ? { viewRoundDecimals: decimals } : undefined
    );

    if (isArea) {
      return convertedValue;
    }

    // Apply decimal rounding after unit conversion
    if (typeof convertedValue === 'number' && decimals !== undefined) {
      return Number(convertedValue.toFixed(decimals));
    }

    return convertedValue;
  };

  // Process logs data into rows mapped to template fields, sorted by date descending
  const processedRows: ProcessedRow[] = useMemo(() => {
    const logs = logsData?.getAggregatedKpiLogEntries ?? [];

    const rows = logs.map((log, index) => {
      const { dayOfWeek, formattedDate } = formatLogDate(
        log.logDate,
        selectedDateAggregation,
        log.startDate || undefined,
        log.endDate || undefined,
        userTimezone
      );

      const fieldValues: Record<string, number | undefined> = {};
      for (const field of templateFields) {
        const key = field.key;
        if (!key) continue;
        fieldValues[key] = getAggregatedKpiFieldNumericValue(log.fields, field);
      }

      return {
        id: `${log.logDate}-${index}`,
        logDate: log.logDate,
        dayOfWeek,
        formattedDate,
        fieldValues,
      };
    });

    return rows;
  }, [logsData?.getAggregatedKpiLogEntries, selectedDateAggregation, templateFields, userTimezone]);

  const hasMoreData = useMemo(() => {
    const logs = logsData?.getAggregatedKpiLogEntries ?? [];
    return logs.length === rowsPerPage;
  }, [logsData, rowsPerPage]);

  const totalCount = useMemo(() => {
    if (hasMoreData) {
      return -1;
    } else {
      return page * rowsPerPage + processedRows.length;
    }
  }, [hasMoreData, page, rowsPerPage, processedRows.length]);

  const fieldUnitsMap: Record<string, SectionUnitsEnum | null | undefined> = useMemo(() => {
    const result: Record<string, SectionUnitsEnum | null | undefined> = {};
    for (const field of templateFields) {
      if (field.key) {
        result[field.key] = field.fieldUnit;
      }
    }
    return result;
  }, [templateFields]);

  const totals: Record<string, number> = useMemo(() => {
    const result: Record<string, number> = {};
    const logs = logsData?.getAggregatedKpiLogEntries ?? [];
    const firstLog = logs[0];

    for (const field of templateFields) {
      const key = field.key;
      if (!key) continue;

      const fieldTotal = firstLog?.fieldTotals.find((row) =>
        aggregatedKpiFieldTotalMatchesColumn(row, field)
      );

      if (fieldTotal?.total !== null && fieldTotal?.total !== undefined) {
        const fieldUnit = field.fieldUnit;
        const decimals = field.roundToDecimal ?? 2;
        result[key] =
          fieldUnit === SectionUnitsEnum.Area
            ? fieldTotal.total
            : Number(fieldTotal.total.toFixed(decimals));
      } else {
        result[key] = 0;
      }
    }

    return result;
  }, [logsData?.getAggregatedKpiLogEntries, templateFields]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  // Prepare columns for export dialog
  const dialogColumns = useMemo(() => {
    return templateFields
      .filter((field) => field.key)
      .map((field) => ({
        name: field.name,
        key: field.key ?? '',
        isSelected: (visibleColumnKeys ?? []).includes(field.key ?? ''),
      }));
  }, [templateFields, visibleColumnKeys]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled', isReportDownload: true });

  const handleExportXlsx = useCallback(
    async (selectedColumnsKeys: string[]) => {
      try {
        await downloadXLSX(selectedColumnsKeys);
        showSnackBar('Export completed successfully!', 'success');
      } catch (error) {
        console.error('Export failed:', error);
        showSnackBar('Failed to export worklog report', 'error');
      }
    },
    [downloadXLSX, showSnackBar]
  );

  const handleOpenExportDialog = useCallback(() => {
    setExportXlsxDialogOpen(true);
  }, []);

  if (isInitialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <PageLoader variant="sm" />
      </Box>
    );
  }

  if (!isInitialLoading && canQueryLogs && error && !hasAggregatedLogsData) {
    return <Alert severity="error">Failed to load worklogs</Alert>;
  }

  if (!selectedWorklogsTemplate) {
    return <Alert severity="info">No templates found for either platform</Alert>;
  }

  return (
    <CardContainer elevation={0} sx={{ mt: 2, position: 'relative' }}>
      {isRefreshing && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          }}
        />
      )}
      <CardHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CardTitle>Detailed worklog report</CardTitle>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ColumnsButton />
          <ExportLogsButton
            onClick={handleOpenExportDialog}
            disabled={!selectedWorklogsTemplate || exportLoading}
          />
          <LogsButton
            id="kpi-tracker-log-work-button"
            structureId={selectedDeploymentIds[0]}
            disabled={!selectedWorklogsTemplate}
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            endIcon={<AbyssIcon iconName="beta" size="md" />}
            sx={containedButtonStyle}
            showLogWork
          />
        </Box>
      </CardHeader>

      <StyledTableContainer sx={{ overflowX: 'auto' }}>
        <StyledTable size="small">
          <TableHead>
            <TableRow>
              {/* Date column - always visible */}
              <HeaderCell>Date</HeaderCell>

              {/* Dynamic columns from visible fields */}
              {visibleFields.map((field) => {
                const key = field.key;
                if (!key) return null;
                return (
                  <HeaderCell key={field.id} align="right">
                    {field.name}
                    {fieldUnitsMap[key] &&
                      fieldUnitsMap[key] !== 'NA' &&
                      ` (${getSectionUnitSymbol(fieldUnitsMap[key], unitSystem)})`}
                  </HeaderCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {processedRows.map((row) => (
              <TableRow key={row.id}>
                {/* Date cell - always visible */}
                <BodyCell>
                  <DateCellContent>
                    <DayText>{row.dayOfWeek}</DayText>
                    <DateText>{row.formattedDate}</DateText>
                  </DateCellContent>
                </BodyCell>

                {visibleFields.map((field) => {
                  const key = field.key;
                  if (!key) return null;
                  const fieldUnit = fieldUnitsMap[key];
                  const decimals = field.roundToDecimal ?? 2;
                  return (
                    <BodyCell key={field.id} align="right">
                      {getDisplayValue(row.fieldValues[key], fieldUnit, decimals)}
                    </BodyCell>
                  );
                })}
              </TableRow>
            ))}

            {/* Totals row */}
            <TotalRow>
              {/* Total label */}
              <BodyCell>
                <Typography
                  sx={{ fontSize: '1.4rem', fontWeight: 500, color: abyssColors.primary[700] }}
                >
                  Total
                </Typography>
              </BodyCell>
              {visibleFields.map((field) => {
                const key = field.key;
                if (!key) return null;
                const fieldUnit = fieldUnitsMap[key];
                const decimals = field.roundToDecimal ?? 2;
                return (
                  <BodyCell key={field.id} align="right">
                    {getDisplayValue(totals[key], fieldUnit, decimals)}
                  </BodyCell>
                );
              })}
            </TotalRow>
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        id="kpi-tracker-table-pagination"
        slotProps={{
          select: {
            id: 'kpi-tracker-rows-per-page-select',
          },
          actions: {
            previousButton: {
              id: 'kpi-tracker-pagination-previous',
            },
            nextButton: {
              id: 'kpi-tracker-pagination-next',
            },
          },
        }}
      />

      <ColumnsSelectionDialog
        open={exportXlsxDialogOpen}
        columns={dialogColumns}
        heading="Export Detailed Worklog Report"
        subHeading="The logs will be exported as an XLSX file based on your currently applied filters and selected columns."
        applyButtonText="Export"
        applyButtonLoadingText="Exporting..."
        successMessage="The selected columns have been successfully exported as a .xlsx file."
        errorMessage="Failed to export Excel file"
        userPreferenceKey={selectedWorklogsTemplate?.id}
        userPreferenceType="xlsx"
        onApply={handleExportXlsx}
        onClose={() => setExportXlsxDialogOpen(false)}
      />

      <LogWorkDrawer />
    </CardContainer>
  );
};
